import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { errorMessage } from '@/lib/api/client';
import { Seo } from '@/lib/seo/Seo';
import { useAuth } from '@/features/auth/AuthContext';
import { postLoginPath } from '@/pages/admin/lib/staffPanelPath';
import { DEMO_LOGIN_ACCOUNTS } from '@/pages/admin/demo/auth/demoAuth';
import {
  AUTH_EYE,
  AUTH_FIELD,
  AUTH_LINK,
  AUTH_PRIMARY,
  AuthField,
  AuthShell,
} from '@/pages/public/auth/AuthShell';

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!loading && user) {
    return <Navigate to={postLoginPath(user.role, from)} replace />;
  }

  function fillDemo(account: (typeof DEMO_LOGIN_ACCOUNTS)[number]) {
    setEmail(account.email);
    setPassword(account.password);
    setError(null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const next = await login(email, password);
      navigate(postLoginPath(next.role, from), { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo title="Log in" path="/login" desc="Log in to your MAKTech Academy account." />
      <AuthShell
        title="Login"
        backTo="/"
        footer={
          <>
            Don&apos;t have an account?{' '}
            <Link to="/register" className={AUTH_LINK}>
              Register for free
            </Link>
          </>
        }
      >
        <form onSubmit={(e) => void submit(e)} data-testid="login-form">
          <AuthField label="Email" htmlFor="login-email">
            <input
              id="login-email"
              className={AUTH_FIELD}
              type="email"
              autoComplete="email"
              placeholder="username@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email"
            />
          </AuthField>
          <AuthField label="Password" htmlFor="login-password">
            <div className="relative">
              <input
                id="login-password"
                className={`${AUTH_FIELD} pr-11`}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
              />
              <button
                type="button"
                className={AUTH_EYE}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? <FiEyeOff className="size-4" aria-hidden /> : <FiEye className="size-4" aria-hidden />}
              </button>
            </div>
          </AuthField>

          {error ? (
            <p className="mb-3 text-[13.5px] text-[#ffe0d6]" data-testid="login-error">
              {error}
            </p>
          ) : null}

          <button type="submit" className={AUTH_PRIMARY} disabled={busy} data-testid="login-submit">
            {busy ? 'Please wait…' : 'Sign in'}
          </button>
        </form>

        <div className="mt-5 rounded-2xl border border-white/25 bg-black/20 p-3.5" data-testid="demo-logins">
          <p className="mb-2.5 text-[11px] font-bold tracking-[.06em] text-white/70 uppercase">
            Demo logins — tap to fill
          </p>
          <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
            {DEMO_LOGIN_ACCOUNTS.map((account) => (
              <li key={account.email}>
                <button
                  type="button"
                  onClick={() => fillDemo(account)}
                  className="flex w-full cursor-pointer items-start justify-between gap-2 rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-left transition hover:border-white/35 hover:bg-white/18"
                  data-testid={`demo-login-${account.role.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <span className="min-w-0">
                    <span className="block text-[12.5px] font-semibold text-white">{account.role}</span>
                    <span className="block truncate text-[11.5px] text-white/70">{account.email}</span>
                  </span>
                  <span className="shrink-0 font-mono text-[11px] text-white/85">{account.password}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </AuthShell>
    </>
  );
}
